import React from 'react';
import { motion } from 'framer-motion';
import { TrophyIcon } from 'lucide-react';
interface Match {
  team1: string;
  team2: string;
  winner: string;
}
export function TournamentBracket() {
  const quarterfinals: Match[] = [{
    team1: 'Obsidian Wolves',
    team2: 'Crimson Drift',
    winner: 'Obsidian Wolves'
  }, {
    team1: 'Phantom Reapers',
    team2: 'Neon Vanguard',
    winner: 'Neon Vanguard'
  }, {
    team1: 'Iron Tempest',
    team2: 'Solar Wraiths',
    winner: 'Iron Tempest'
  }, {
    team1: 'Void Sentinels',
    team2: 'Ember Collective',
    winner: 'Void Sentinels'
  }];
  const semifinals: Match[] = [{
    team1: 'Obsidian Wolves',
    team2: 'Neon Vanguard',
    winner: 'Obsidian Wolves'
  }, {
    team1: 'Iron Tempest',
    team2: 'Void Sentinels',
    winner: 'Iron Tempest'
  }];
  const finals: Match = {
    team1: 'Obsidian Wolves',
    team2: 'Iron Tempest',
    winner: 'Iron Tempest'
  };
  const MatchCard = ({
    match,
    delay = 0
  }: {
    match: Match;
    delay?: number;
  }) => <motion.div initial={{
    opacity: 0,
    x: -20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4,
    delay
  }} className="bg-gray-900/80 border border-gray-800 rounded-lg overflow-hidden min-w-[180px]" data-id="element-237">
      <div className={`px-3 py-2 text-sm font-medium border-b border-gray-800 flex items-center justify-between ${match.winner === match.team1 ? 'bg-cyan-500/10 text-cyan-400 border-l-2 border-l-cyan-500' : 'text-gray-400'}`} data-id="element-238">
        <span className="truncate" data-id="element-239">{match.team1}</span>
        {match.winner === match.team1 && <span className="text-xs ml-2" data-id="element-240">✓</span>}
      </div>
      <div className={`px-3 py-2 text-sm font-medium flex items-center justify-between ${match.winner === match.team2 ? 'bg-cyan-500/10 text-cyan-400 border-l-2 border-l-cyan-500' : 'text-gray-400'}`} data-id="element-241">
        <span className="truncate" data-id="element-242">{match.team2}</span>
        {match.winner === match.team2 && <span className="text-xs ml-2" data-id="element-243">✓</span>}
      </div>
    </motion.div>;
  return <section id="brackets" className="relative py-24 sm:py-32 overflow-hidden" data-id="element-244">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950" data-id="element-245" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-246">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-247">
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-white mb-4" data-id="element-248">
            BRACKET <span className="text-fuchsia-500" data-id="element-249">WARFARE</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-250">
            Run single-elim, double-elim, or round-robin tournaments with
            automated seeding and live results.
          </p>
        </motion.div>

        {/* Tournament Name */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-center mb-8" data-id="element-251">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-400 font-display font-bold uppercase tracking-wider text-sm" data-id="element-252">
            <TrophyIcon className="w-4 h-4" data-id="element-253" />
            AEGIS INVITATIONAL — Season 4
          </span>
        </motion.div>

        {/* Bracket Container */}
        <div className="overflow-x-auto pb-4" data-id="element-254">
          <div className="min-w-[900px] flex items-center justify-center gap-8 lg:gap-12" data-id="element-255">
            {/* Quarterfinals */}
            <div className="flex flex-col gap-4" data-id="element-256">
              <div className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-257">
                Quarterfinals
              </div>
              <div className="flex flex-col gap-8" data-id="element-258">
                {quarterfinals.map((match, index) => <MatchCard key={`qf-${index}`} match={match} delay={index * 0.1} data-id="element-259" />)}
              </div>
            </div>

            {/* Connector Lines QF to SF */}
            <div className="flex flex-col gap-8 py-8" data-id="element-260">
              <svg width="40" height="120" className="text-cyan-500/30" data-id="element-261">
                <path d="M0 30 L20 30 L20 90 L40 90" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-262" />
                <path d="M0 90 L20 90 L20 30 L40 30" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-263" />
              </svg>
              <svg width="40" height="120" className="text-cyan-500/30" data-id="element-264">
                <path d="M0 30 L20 30 L20 90 L40 90" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-265" />
                <path d="M0 90 L20 90 L20 30 L40 30" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-266" />
              </svg>
            </div>

            {/* Semifinals */}
            <div className="flex flex-col gap-4" data-id="element-267">
              <div className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-268">
                Semifinals
              </div>
              <div className="flex flex-col gap-32" data-id="element-269">
                {semifinals.map((match, index) => <MatchCard key={`sf-${index}`} match={match} delay={0.4 + index * 0.1} data-id="element-270" />)}
              </div>
            </div>

            {/* Connector Lines SF to Finals */}
            <div className="flex items-center" data-id="element-271">
              <svg width="40" height="200" className="text-cyan-500/30" data-id="element-272">
                <path d="M0 50 L20 50 L20 100 L40 100" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-273" />
                <path d="M0 150 L20 150 L20 100 L40 100" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-274" />
              </svg>
            </div>

            {/* Finals */}
            <div className="flex flex-col gap-4" data-id="element-275">
              <div className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-276">
                Finals
              </div>
              <MatchCard match={finals} delay={0.6} data-id="element-277" />
            </div>

            {/* Winner Line */}
            <div className="flex items-center" data-id="element-278">
              <svg width="40" height="60" className="text-lime-500/50" data-id="element-279">
                <path d="M0 30 L40 30" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-280" />
              </svg>
            </div>

            {/* Winner */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.8
          }} className="flex flex-col items-center" data-id="element-281">
              <div className="text-center text-xs font-bold text-lime-500 uppercase tracking-wider mb-2" data-id="element-282">
                Champion
              </div>
              <div className="relative" data-id="element-283">
                <div className="px-6 py-4 bg-gradient-to-br from-lime-500/20 to-lime-600/10 border-2 border-lime-500 rounded-lg glow-lime" data-id="element-284">
                  <TrophyIcon className="w-8 h-8 text-lime-400 mx-auto mb-2" data-id="element-285" />
                  <span className="font-display font-bold text-lg text-lime-400 whitespace-nowrap" data-id="element-286">
                    {finals.winner}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Mobile hint */}
        <p className="text-center text-sm text-gray-600 mt-4 lg:hidden" data-id="element-287">
          ← Scroll to view full bracket →
        </p>
      </div>
    </section>;
}